import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addAdminSurveyFile,
  changeAdminPassword,
  createAdminProject,
  createAdminUser,
  getAdminMe,
  getAdminSettings,
  getAdminSurvey,
  getAdminSurveyExportData,
  getAdminSurveys,
  getAdminSurveyStats,
  getAdminUsers,
  adminLogin,
  adminLogout,
  requestAdminSignedUpload,
  updateAdminSettings,
  updateAdminSurveyDetails,
  updateAdminSurveyFileStatus,
  updateAdminSurveyStatus,
  uploadFileToSignedUrl
} from './api.js';
import { isImageFileName } from './fileTypes.js';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'valid', label: 'Valid' },
  { value: 'reject', label: 'Reject' }
];

const uploadCategories = [
  { value: 'recipientPhotos', label: 'Foto Penerima' },
  { value: 'documentPhotos', label: 'Foto Dokumen' },
  { value: 'doResiPhotos', label: 'Foto DO + RESI AJC' },
  { value: 'additionalPhotos', label: 'Foto Tambahan' }
];

const signedUrlTtlOptions = [
  { value: '15m', label: '15 menit' },
  { value: '30m', label: '30 menit' },
  { value: '1d', label: '1 hari' },
  { value: '7d', label: '7 hari' },
  { value: '14d', label: '14 hari' },
  { value: '30d', label: '30 hari' }
];

function statusLabel(value) {
  return statusOptions.find((status) => status.value === value)?.label || 'Pending';
}

function canEdit(user) {
  return user?.role === 'superadmin' || user?.role === 'user';
}

function fileKind(file = {}) {
  const type = String(file.contentType || '').toLowerCase();
  const name = String(file.fileName || '').toLowerCase();
  if (type.startsWith('image/') || isImageFileName(name)) return 'image';
  if (type.startsWith('video/') || /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(name)) return 'video';
  return 'document';
}

function activeFiles(files = []) {
  return files.filter((file) => file.status !== 'rejected');
}

function groupedActiveFiles(submission) {
  return uploadCategories.map((category) => ({
    ...category,
    files: activeFiles(submission.files?.[category.value] || [])
  }));
}

function fileUrls(files = []) {
  return files.map((file) => fileUrl(file)).filter(Boolean);
}

function categoryLinkColumns(submissions) {
  return uploadCategories.flatMap((category) => {
    const count = Math.max(
      1,
      ...submissions.map((submission) => fileUrls(activeFiles(submission.files?.[category.value] || [])).length)
    );
    return Array.from({ length: count }, (_, index) => ({
      category,
      index,
      key: `link_${category.value}_${index + 1}`,
      header: count === 1 ? `${category.label} Link` : `${category.label} Link ${index + 1}`
    }));
  });
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function downloadTextFile(content, fileName, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function fileUrl(file = {}) {
  return file.viewUrl || file.readUrl || file.downloadUrl || '';
}

function thumbnailUrl(file, size = 240, quality = 68) {
  const url = file.thumbnailUrl || file.readUrl || file.viewUrl || '';
  if (!url || !file.thumbnailUrl) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}size=${encodeURIComponent(size)}&quality=${encodeURIComponent(quality)}`;
}

function fileExtension(file = {}) {
  const name = String(file.fileName || 'file');
  const extension = name.includes('.') ? name.split('.').pop() : 'FILE';
  return extension.slice(0, 5).toUpperCase();
}

function exportFileName(extension) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `survey-export-${stamp}.${extension}`;
}

function buildCsv(submissions) {
  const linkColumns = categoryLinkColumns(submissions);
  const headers = [
    'Submitted',
    'Project',
    'AWB',
    'Penerima',
    'Relasi Penerima',
    'Proof Code',
    'Status',
    'Geostamp',
    ...linkColumns.map((column) => column.header)
  ];
  const lines = submissions.map((submission) => {
    const groups = groupedActiveFiles(submission);
    const urlsByCategory = Object.fromEntries(groups.map((group) => [group.value, fileUrls(group.files)]));
    return [
      submission.submittedAt || submission.submittedAtIso,
      submission.project?.name || 'No Project',
      submission.awb,
      submission.recipientName,
      submission.recipientRelation,
      submission.proofCode,
      statusLabel(submission.status),
      submission.location?.geostampText,
      ...linkColumns.map((column) => urlsByCategory[column.category.value]?.[column.index] || '')
    ].map(csvCell).join(',');
  });
  return `\uFEFF${[headers.map(csvCell).join(','), ...lines].join('\r\n')}`;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fetchImageDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
  return blobToDataUrl(await response.blob());
}

async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}

function exportImageFiles(submission) {
  return uploadCategories.flatMap((category) =>
    activeFiles(submission.files?.[category.value] || [])
      .filter((file) => fileKind(file) === 'image')
      .map((file) => ({ ...file, category: category.value, categoryLabel: category.label }))
  );
}

async function thumbnailDataUrl(file, { size, quality }) {
  return fetchImageDataUrl(thumbnailUrl(file, size, quality));
}

async function exportImages(submissions, imageOptions, onProgress) {
  const jobs = submissions.flatMap((submission) =>
    exportImageFiles(submission).map((file) => ({ submissionId: submission.id, file }))
  );
  let completed = 0;
  const images = await mapWithConcurrency(jobs, 4, async ({ submissionId, file }) => {
    try {
      return { submissionId, file, src: await thumbnailDataUrl(file, imageOptions) };
    } catch {
      return { submissionId, file, src: '' };
    } finally {
      completed += 1;
      onProgress(`Menyiapkan thumbnail ${completed} dari ${jobs.length}`);
    }
  });
  return images.reduce((groups, image) => {
    if (!groups.has(image.submissionId)) groups.set(image.submissionId, []);
    groups.get(image.submissionId).push(image);
    return groups;
  }, new Map());
}

function excelLink(url) {
  return url ? { text: 'Click here', hyperlink: url, tooltip: url } : '';
}

function styleExcelLink(cell) {
  if (!cell.value?.hyperlink) return;
  cell.font = { color: { argb: 'FF2563EB' }, underline: true };
}

async function buildExcelWorkbook(submissions, imageGroups, onProgress) {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'IDX Survey';
  workbook.created = new Date();
  const worksheet = workbook.addWorksheet('Survey Valid');
  const linkColumns = categoryLinkColumns(submissions);
  const maxPhotoCount = Math.max(1, ...submissions.map((submission) => imageGroups.get(submission.id)?.length || 0));
  const columns = [
    { header: 'Submitted', key: 'submittedAt', width: 22 },
    { header: 'Project', key: 'project', width: 24 },
    { header: 'AWB', key: 'awb', width: 20 },
    { header: 'Penerima', key: 'recipientName', width: 22 },
    { header: 'Relasi Penerima', key: 'recipientRelation', width: 20 },
    { header: 'Proof Code', key: 'proofCode', width: 18 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'Geostamp', key: 'geostamp', width: 42 },
    ...linkColumns.map((column) => ({
      header: column.header,
      key: column.key,
      width: 18
    })),
    ...Array.from({ length: maxPhotoCount }, (_, index) => ({
      header: `Foto ${index + 1}`,
      key: `photo_${index + 1}`,
      width: 24
    }))
  ];
  worksheet.columns = columns;
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
  const photoStartColumn = columns.length - maxPhotoCount;

  for (const [index, submission] of submissions.entries()) {
    onProgress(`Menyusun Excel ${index + 1} dari ${submissions.length}`);
    const groups = groupedActiveFiles(submission);
    const urlsByCategory = Object.fromEntries(groups.map((group) => [group.value, fileUrls(group.files)]));
    const linkCells = Object.fromEntries(
      linkColumns.map((column) => [column.key, excelLink(urlsByCategory[column.category.value]?.[column.index])])
    );
    const row = worksheet.addRow({
      submittedAt: submission.submittedAt || submission.submittedAtIso || '',
      project: submission.project?.name || 'No Project',
      awb: submission.awb || '',
      recipientName: submission.recipientName || '',
      recipientRelation: submission.recipientRelation || '',
      proofCode: submission.proofCode || '',
      status: statusLabel(submission.status),
      geostamp: submission.location?.geostampText || '',
      ...linkCells
    });
    row.alignment = { vertical: 'top', wrapText: true };
    linkColumns.forEach((column) => styleExcelLink(row.getCell(column.key)));
    const images = imageGroups.get(submission.id) || [];
    for (const [photoIndex, { file, src }] of images.entries()) {
      const columnIndex = photoStartColumn + photoIndex;
      if (src) {
        const imageId = workbook.addImage({ base64: src, extension: 'jpeg' });
        worksheet.addImage(imageId, {
          tl: { col: columnIndex, row: row.number - 1 },
          ext: { width: 160, height: 160 }
        });
      } else {
        row.getCell(`photo_${photoIndex + 1}`).value = file.fileName || 'image';
      }
    }
    if (images.length) row.height = 124;
  }
  return workbook.xlsx.writeBuffer();
}

async function buildPdfBlob(submissions, imageGroups, onProgress) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 28;
  const imageSize = 110;
  const gap = 12;
  const contentWidth = pageWidth - margin * 2;

  function ensureSpace(requiredHeight, y) {
    if (y + requiredHeight <= pageHeight - margin) return y;
    doc.addPage();
    return margin;
  }

  function labelValue(label, value, x, y, width = 240) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(label, x, y);
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    const lines = doc.splitTextToSize(String(value || '-'), width);
    doc.text(lines, x, y + 12);
    return y + 14 + lines.length * 10;
  }

  for (const [index, submission] of submissions.entries()) {
    onProgress(`Menyusun PDF ${index + 1} dari ${submissions.length}`);
    if (index > 0) doc.addPage();
    let y = margin;
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(margin, y, contentWidth, 54, 4, 4, 'F');
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text(submission.awb || '-', margin + 14, y + 22);
    doc.setFontSize(9);
    doc.text(statusLabel(submission.status), pageWidth - margin - 80, y + 22, { align: 'right' });
    doc.setTextColor(71, 85, 105);
    doc.text(`${submission.project?.name || 'No Project'} | ${submission.submittedAt || submission.submittedAtIso || '-'}`, margin + 14, y + 40);
    y += 72;

    const leftY = labelValue('Penerima', submission.recipientName, margin, y);
    const rightY = labelValue('Relasi Penerima', submission.recipientRelation, margin + contentWidth / 2, y, contentWidth / 2 - 8);
    y = Math.max(leftY, rightY);
    y = Math.max(y, labelValue('Proof Code', submission.proofCode, margin, y + 4, contentWidth));

    if (submission.location?.geostampText) {
      y = ensureSpace(54, y + 4);
      doc.setFillColor(248, 250, 252);
      const lines = doc.splitTextToSize(submission.location.geostampText, contentWidth - 20);
      const boxHeight = Math.max(44, 24 + lines.length * 10);
      doc.roundedRect(margin, y, contentWidth, boxHeight, 3, 3, 'F');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('Geostamp', margin + 10, y + 14);
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(lines, margin + 10, y + 28);
      y += boxHeight + 14;
    }

    const linkRows = groupedActiveFiles(submission).flatMap((group) =>
      fileUrls(group.files).map((url, urlIndex) => ({
        label: group.files.length > 1 ? `${group.label} ${urlIndex + 1}` : group.label,
        url
      }))
    );
    if (linkRows.length) {
      y = ensureSpace(28 + linkRows.length * 14, y);
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text('Link Dokumen', margin, y);
      y += 16;
      for (const linkRow of linkRows) {
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text(`${linkRow.label}:`, margin, y);
        doc.setTextColor(37, 99, 235);
        doc.textWithLink('Click here', margin + 110, y, { url: linkRow.url });
        y += 14;
      }
      y += 8;
    }

    const images = imageGroups.get(submission.id) || [];
    if (images.length) {
      y = ensureSpace(136, y);
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text('Foto', margin, y);
      y += 14;
    }
    let x = margin;
    for (const { file, src } of images) {
      if (y + imageSize + 24 > pageHeight - margin) {
        doc.addPage();
        x = margin;
        y = margin;
      }
      if (src) doc.addImage(src, 'JPEG', x, y, imageSize, imageSize, undefined, 'FAST');
      else doc.rect(x, y, imageSize, imageSize);
      doc.setFontSize(7);
      doc.text(doc.splitTextToSize(file.fileName || file.categoryLabel || 'Foto', imageSize), x, y + imageSize + 10);
      x += imageSize + gap;
      if (x + imageSize > pageWidth - margin) {
        x = margin;
        y += imageSize + 30;
      }
    }
    doc.setTextColor(15, 23, 42);
  }
  return doc.output('blob');
}

const actionLabels = {
  create_submission: 'Survey baru masuk',
  update_details: 'Detail penerima diubah',
  update_status: 'Status survey diubah',
  upload_file: 'Dokumen ditambahkan',
  update_file_status: 'Status dokumen diubah'
};

const fieldLabels = {
  created: 'Data submission',
  recipientName: 'Nama penerima',
  recipientRelation: 'Siapa penerima',
  proofCode: 'Kode bukti',
  phoneNumber: 'No HP',
  courierName: 'Nama kurir',
  submittedAt: 'Waktu submit',
  submittedAtIso: 'Waktu submit ISO',
  awb: 'Scan AWB',
  project: 'Project',
  files: 'Dokumen',
  location: 'Lokasi',
  clientMeta: 'Perangkat pengirim',
  status: 'Status',
  updatedAt: 'Waktu update',
  updatedAtIso: 'Waktu update ISO',
  updatedBy: 'Diupdate oleh'
};

function readableAction(action) {
  return actionLabels[action] || action.replace(/_/g, ' ');
}

function summarizeFilesByCategory(files = {}) {
  const summaries = Object.entries(files || {})
    .map(([category, categoryFiles]) => {
      const label = uploadCategories.find((item) => item.value === category)?.label || category;
      const names = (categoryFiles || []).map((file) => file.fileName || 'file');
      return names.length ? `${label}: ${names.join(', ')}` : '';
    })
    .filter(Boolean);
  return summaries.length ? summaries.join('\n') : '-';
}

function readableValue(field, value) {
  if (value === undefined || value === null || value === '') return '-';
  if (field === 'status') return statusLabel(value);
  if (field === 'files') return summarizeFilesByCategory(value);
  if (field === 'project') return value?.name || 'No Project';
  if (field === 'location') return value.locationDetailText || value.geostampText || 'Lokasi tersimpan';
  if (field === 'clientMeta') return [value.userAgent, value.language, value.screen].filter(Boolean).join('\n') || '-';
  if (typeof value === 'object') {
    if (value.awb || value.recipientName || value.proofCode) {
      return [
        value.awb && `AWB: ${value.awb}`,
        value.recipientName && `Nama penerima: ${value.recipientName}`,
        value.recipientRelation && `Siapa penerima: ${value.recipientRelation}`,
        value.proofCode && `Kode bukti: ${value.proofCode}`,
        value.status && `Status: ${statusLabel(value.status)}`
      ]
        .filter(Boolean)
        .join('\n');
    }
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function auditChanges(log) {
  const changes = log.changes || {};
  return Object.entries(changes).map(([field, change]) => ({
    field,
    label: fieldLabels[field] || field,
    before: readableValue(field, change?.before),
    after: readableValue(field, change?.after)
  }));
}

function AdminApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminMe()
      .then((result) => setUser(result.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function handleLogout() {
    await adminLogout();
    setUser(null);
  }

  if (loading) return <div className="admin-screen"><div className="admin-panel">Loading...</div></div>;
  if (!user) return <LoginScreen onLogin={setUser} error={error} setError={setError} />;

  return <AdminDashboard user={user} onLogout={handleLogout} />;
}

function LoginScreen({ onLogin, error, setError }) {
  const [email, setEmail] = useState('superadmin@idx-survey.local');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await adminLogin({ email, password });
      onLogin(result.user);
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-screen">
      <form className="admin-panel login-panel" onSubmit={submit}>
        <h1>Backend Survey</h1>
        <p>Login untuk melihat hasil survey dan mengelola user.</p>
        <label>
          Email
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="username" />
        </label>
        <label>
          Password
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" />
        </label>
        {error && <div className="admin-error">{error}</div>}
        <button type="submit" disabled={busy}>{busy ? 'Checking...' : 'Login'}</button>
      </form>
    </div>
  );
}

function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState('surveys');

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div>
          <h1>IDX Survey</h1>
          <span>{user.email}</span>
          <strong>{user.role}</strong>
        </div>
        <nav>
          <button className={tab === 'surveys' ? 'active' : ''} onClick={() => setTab('surveys')} type="button">Survey</button>
          <button className={tab === 'password' ? 'active' : ''} onClick={() => setTab('password')} type="button">Change Password</button>
          {user.role === 'superadmin' && (
            <>
              <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')} type="button">Settings</button>
              <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')} type="button">Users</button>
            </>
          )}
        </nav>
        <button className="logout-btn" onClick={onLogout} type="button">Logout</button>
      </aside>

      <main className="admin-main">
        {tab === 'surveys' && <SurveyResults user={user} />}
        {tab === 'settings' && user.role === 'superadmin' && <SettingsManagement />}
        {tab === 'password' && <ChangePassword />}
        {tab === 'users' && user.role === 'superadmin' && <UserManagement />}
      </main>
    </div>
  );
}

function SurveyResults({ user }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, valid: 0, reject: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1, offset: 0, returned: 0, limit: 100 });
  const [selected, setSelected] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', awb: '', status: user.role === 'view' ? 'valid' : 'all', projectId: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportState, setExportState] = useState({ active: false, format: '', message: '', error: '' });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const effectiveFilters = useMemo(() => ({ ...filters, limit: pageSize, page }), [filters, page, pageSize]);
  const selectedProjectName = useMemo(() => {
    if (user.role === 'view') return settings?.activeProject?.name || 'No Project';
    if (filters.projectId === 'all') return 'All Project';
    return projects.find((project) => project.id === filters.projectId)?.name || 'No Project';
  }, [filters.projectId, projects, settings?.activeProject?.name, user.role]);
  const exportAvailable = user.role === 'superadmin' || settings?.exportEnabled !== false;
  const exportDisabled = exportState.active || loading || !items.length;

  const load = useCallback(async (nextFilters) => {
    setLoading(true);
    setError('');
    try {
      const [surveyResult, statsResult] = await Promise.all([
        getAdminSurveys(nextFilters),
        getAdminSurveyStats(nextFilters)
      ]);
      setItems(surveyResult.items);
      setPagination(surveyResult.pagination || { total: surveyResult.items?.length || 0, page: 1, totalPages: 1, offset: 0, returned: surveyResult.items?.length || 0, limit: nextFilters.limit || 100 });
      setPage(surveyResult.pagination?.page || 1);
      setStats(statsResult.stats);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  async function openDetail(id) {
    setError('');
    try {
      const result = await getAdminSurvey(id);
      setSelected(result.submission);
      setAuditLogs(result.auditLogs || []);
    } catch (detailError) {
      setError(detailError.message);
    }
  }

  async function refreshSelected() {
    if (selected?.id) await openDetail(selected.id);
    await load(effectiveFilters);
  }

  function submitFilters(event) {
    event.preventDefault();
    setPage(1);
    load({ ...filters, limit: pageSize, page: 1 });
  }

  function updateFilters(patch) {
    setPage(1);
    setFilters((current) => ({ ...current, ...patch }));
  }

  function changePageSize(nextPageSize) {
    setPageSize(nextPageSize);
    setPage(1);
    load({ ...filters, limit: nextPageSize, page: 1 });
  }

  function changePage(nextPage) {
    const normalizedPage = Math.min(Math.max(nextPage, 1), pagination.totalPages || 1);
    setPage(normalizedPage);
    load({ ...filters, limit: pageSize, page: normalizedPage });
  }

  async function exportSurveys(format) {
    if (exportState.active || loading || !items.length) return;
    const label = format.toUpperCase();
    setExportState({ active: true, format: label, message: 'Menyiapkan data export...', error: '' });
    try {
      const { limit: _limit, page: _page, ...filtersWithoutLimit } = effectiveFilters;
      const exportFilters = { ...filtersWithoutLimit, status: 'valid' };
      const { items: submissions } = await getAdminSurveyExportData(exportFilters);
      if (!submissions.length) throw new Error('Tidak ada data valid untuk export.');

      if (format === 'excel') {
        const imageGroups = await exportImages(submissions, { size: 240, quality: 68 }, (message) =>
          setExportState({ active: true, format: label, message, error: '' })
        );
        const buffer = await buildExcelWorkbook(submissions, imageGroups, (message) =>
          setExportState({ active: true, format: label, message, error: '' })
        );
        downloadBlob(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), exportFileName('xlsx'));
        setExportState({ active: false, format: '', message: '', error: '' });
        return;
      }

      if (format === 'pdf') {
        const imageGroups = await exportImages(submissions, { size: 150, quality: 58 }, (message) =>
          setExportState({ active: true, format: label, message, error: '' })
        );
        const blob = await buildPdfBlob(submissions, imageGroups, (message) =>
          setExportState({ active: true, format: label, message, error: '' })
        );
        downloadBlob(blob, exportFileName('pdf'));
        setExportState({ active: false, format: '', message: '', error: '' });
        return;
      }

      if (format === 'csv') {
        setExportState({ active: true, format: label, message: 'Membuat CSV tanpa foto...', error: '' });
        downloadTextFile(buildCsv(submissions), exportFileName('csv'), 'text/csv;charset=utf-8');
      }

      setExportState({ active: false, format: '', message: '', error: '' });
    } catch (exportError) {
      setExportState({ active: false, format: '', message: '', error: exportError.message });
    }
  }

  useEffect(() => {
    async function init() {
      setLoading(true);
      setError('');
      try {
        const settingsResult = await getAdminSettings();
        const activeProjectFilter = settingsResult.settings.activeProject?.id || '__no_project__';
        const initialStatus = user.role === 'view' ? 'valid' : 'all';
        const initialFilters = { dateFrom: '', dateTo: '', awb: '', status: initialStatus, projectId: activeProjectFilter, limit: '100', page: 1 };
        setSettings(settingsResult.settings);
        setProjects(settingsResult.projects || []);
        setFilters({ dateFrom: '', dateTo: '', awb: '', status: initialStatus, projectId: activeProjectFilter });
        await load(initialFilters);
      } catch (initError) {
        setError(initError.message);
        setLoading(false);
      }
    }
    init();
  }, [load, user.role]);

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <h2>Dashboard Survey</h2>
          {settings && <p>PROJECT: {selectedProjectName}</p>}
        </div>
        <div className="section-actions">
          <button type="button" onClick={() => load(effectiveFilters)} disabled={exportState.active}>Refresh</button>
          {exportAvailable && (
            <div
              className="export-menu"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setExportMenuOpen(false);
              }}
            >
              <button
                type="button"
                className="export-menu-trigger"
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exportDisabled}
                aria-haspopup="menu"
                aria-expanded={exportMenuOpen}
              >
                Export
                <span aria-hidden="true" />
              </button>
              {exportMenuOpen && (
                <div className="export-menu-list" role="menu">
                  {[
                    ['excel', 'Excel (.xlsx)'],
                    ['csv', 'CSV'],
                    ['pdf', 'PDF']
                  ].map(([format, label]) => (
                    <button
                      key={format}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setExportMenuOpen(false);
                        exportSurveys(format);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <StatsCards stats={stats} />

      <form className="filter-panel admin-panel" onSubmit={submitFilters}>
        <label>
          Dari
          <input type="date" value={filters.dateFrom} onChange={(event) => updateFilters({ dateFrom: event.target.value })} />
        </label>
        <label>
          Sampai
          <input type="date" value={filters.dateTo} onChange={(event) => updateFilters({ dateTo: event.target.value })} />
        </label>
        <label>
          AWB
          <input value={filters.awb} onChange={(event) => updateFilters({ awb: event.target.value })} placeholder="Cari AWB" />
        </label>
        {user.role !== 'view' && (
          <label>
            Status
            <select value={filters.status} onChange={(event) => updateFilters({ status: event.target.value })}>
              <option value="all">All</option>
              {statusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </label>
        )}
        {user.role !== 'view' && (
          <label>
            Project
            <select value={filters.projectId} onChange={(event) => updateFilters({ projectId: event.target.value })}>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
              <option value="all">All Project</option>
            </select>
          </label>
        )}
        <button type="submit">Apply</button>
      </form>

      {error && <div className="admin-error">{error}</div>}
      {exportState.error && <div className="admin-error">{exportState.error}</div>}
      {loading ? (
        <div className="admin-panel">Loading survey...</div>
      ) : (
        <div className="result-grid">
          <SurveyTable
            items={items}
            selectedId={selected?.id}
            onOpen={openDetail}
            pagination={pagination}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            onPageChange={changePage}
          />
          <SurveyDetail
            user={user}
            submission={selected}
            auditLogs={auditLogs}
            onChanged={refreshSelected}
          />
        </div>
      )}
      {exportState.active && <ExportProgressModal format={exportState.format} message={exportState.message} />}
    </section>
  );
}

function ExportProgressModal({ format, message }) {
  return (
    <div className="admin-modal-backdrop" role="dialog" aria-modal="true" aria-label="Export sedang diproses">
      <div className="admin-modal-panel export-progress-modal">
        <div className="export-spinner" aria-hidden="true" />
        <h3>Export {format} sedang diproses</h3>
        <p>{message}</p>
        <small>Mohon tunggu sampai proses selesai. Tombol export dikunci sementara.</small>
      </div>
    </div>
  );
}

function StatsCards({ stats }) {
  return (
    <div className="stats-grid">
      <div className="stat-card"><span>Total Masuk</span><strong>{stats.total}</strong></div>
      <div className="stat-card"><span>Pending</span><strong>{stats.pending}</strong></div>
      <div className="stat-card"><span>Valid</span><strong>{stats.valid}</strong></div>
      <div className="stat-card"><span>Rejected</span><strong>{stats.reject}</strong></div>
    </div>
  );
}

function SurveyTable({ items, selectedId, onOpen, pagination, pageSize, onPageSizeChange, onPageChange }) {
  const total = pagination?.total || 0;
  const returned = pagination?.returned || items.length;
  const start = total && returned ? (pagination.offset || 0) + 1 : 0;
  const end = total && returned ? (pagination.offset || 0) + returned : 0;
  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || 1;

  return (
    <div className="admin-panel table-panel">
      <div className="table-toolbar">
        <label>
          Tampilkan
          <select value={pageSize} onChange={(event) => onPageSizeChange(event.target.value)}>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="500">500</option>
          </select>
        </label>
        <span>{start}-{end} dari {total} data</span>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Project</th>
              <th>AWB</th>
              <th>Penerima</th>
              <th>Proof</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr className={item.id === selectedId ? 'selected' : ''} key={item.id} onClick={() => onOpen(item.id)}>
                <td>{item.submittedAt || item.submittedAtIso}</td>
                <td>{item.project?.name || 'No Project'}</td>
                <td>{item.awb}</td>
                <td>{item.recipientName}</td>
                <td>{item.proofCode}</td>
                <td><StatusBadge status={item.status} /></td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan="6" className="empty-table-cell">Tidak ada data.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination-bar">
        <button type="button" onClick={() => onPageChange(currentPage - 1)} disabled={!pagination?.hasPrevious}>Previous</button>
        <span>Halaman {currentPage} dari {totalPages}</span>
        <button type="button" onClick={() => onPageChange(currentPage + 1)} disabled={!pagination?.hasNext}>Next</button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`status-badge ${status || 'pending'}`}>{statusLabel(status)}</span>;
}

function SurveyDetail({ user, submission, auditLogs, onChanged }) {
  const editable = canEdit(user);
  if (!submission) return <div className="admin-panel detail-panel">Pilih survey untuk melihat detail.</div>;

  return (
    <div className="admin-panel detail-panel">
      <div className="detail-title">
        <div>
          <h3>{submission.awb}</h3>
          <p className="detail-project">{submission.project?.name || 'No Project'}</p>
          <StatusBadge status={submission.status} />
        </div>
        {editable && <StatusActions id={submission.id} currentStatus={submission.status} onChanged={onChanged} />}
      </div>

      <EditableSurveyForm user={user} submission={submission} onChanged={onChanged} />

      <h4>Location Detail</h4>
      <pre>{submission.location?.locationDetailText}</pre>
      <h4>Geostamp</h4>
      <pre>{submission.location?.geostampText}</pre>

      <h4>Dokumen</h4>
      <DocumentGroups user={user} submission={submission} onChanged={onChanged} />

      {user.role !== 'view' && (
        <>
          <h4>Audit Log</h4>
          <AuditLog logs={auditLogs} />
        </>
      )}
    </div>
  );
}

function EditableSurveyForm({ user, submission, onChanged }) {
  const editable = canEdit(user);
  const showSensitive = user.role !== 'view';
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    recipientName: submission.recipientName || '',
    recipientRelation: submission.recipientRelation || ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      recipientName: submission.recipientName || '',
      recipientRelation: submission.recipientRelation || ''
    });
    setIsEditing(false);
    setMessage('');
    setError('');
  }, [submission.id, submission.recipientName, submission.recipientRelation]);

  async function save(event) {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      await updateAdminSurveyDetails(submission.id, {
        recipientName: form.recipientName,
        recipientRelation: form.recipientRelation
      });
      setMessage('Data diupdate. Status kembali menjadi Pending.');
      setIsEditing(false);
      await onChanged();
    } catch (saveError) {
      setError(saveError.message);
    }
  }

  function cancelEdit() {
    setForm({
      recipientName: submission.recipientName || '',
      recipientRelation: submission.recipientRelation || ''
    });
    setIsEditing(false);
    setError('');
  }

  if (!isEditing) {
    return (
      <>
        <dl>
          <dt>Nama Penerima</dt>
          <dd>{submission.recipientName}</dd>
          <dt>Siapa Penerima</dt>
          <dd>{submission.recipientRelation}</dd>
          {showSensitive && (
            <>
              <dt>No HP</dt>
              <dd>{submission.phoneNumber || '-'}</dd>
              <dt>Nama Kurir</dt>
              <dd>{submission.courierName || '-'}</dd>
            </>
          )}
          <dt>Proof Code</dt>
          <dd>{submission.proofCode}</dd>
          <dt>Submitted</dt>
          <dd>{submission.submittedAt}</dd>
        </dl>
        {error && <div className="admin-error">{error}</div>}
        {message && <div className="admin-success">{message}</div>}
        {editable && (
          <div className="detail-actions">
            <button type="button" onClick={() => setIsEditing(true)}>Edit</button>
          </div>
        )}
      </>
    );
  }

  return (
    <form className="inline-edit-form" onSubmit={save}>
      <label>
        Nama Penerima
        <input value={form.recipientName} onChange={(event) => setForm({ ...form, recipientName: event.target.value })} />
      </label>
      <label>
        Tipe Penerima
        <select value={form.recipientRelation} onChange={(event) => setForm({ ...form, recipientRelation: event.target.value })}>
          <option value="">Please Select</option>
          <option value="Penerima Asli">Penerima Asli</option>
          <option value="Keluarga Inti">Keluarga Inti</option>
        </select>
      </label>
      {showSensitive && (
        <>
          <div className="readonly-field">
            No HP
            <strong>{submission.phoneNumber || '-'}</strong>
          </div>
          <div className="readonly-field">
            Nama Kurir
            <strong>{submission.courierName || '-'}</strong>
          </div>
        </>
      )}
      <div className="form-row-full">
        <strong>Proof Code</strong>
        <span>{submission.proofCode}</span>
      </div>
      {error && <div className="admin-error form-row-full">{error}</div>}
      {message && <div className="admin-success form-row-full">{message}</div>}
      <button type="submit">Save Data</button>
      <button type="button" className="secondary-action" onClick={cancelEdit}>Cancel</button>
    </form>
  );
}

function StatusActions({ id, currentStatus, onChanged }) {
  const [busy, setBusy] = useState('');

  async function setStatus(status) {
    setBusy(status);
    try {
      await updateAdminSurveyStatus(id, status);
      await onChanged();
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="status-actions">
      {statusOptions.map((status) => (
        <button
          key={status.value}
          type="button"
          className={currentStatus === status.value ? 'active' : ''}
          disabled={busy !== ''}
          onClick={() => setStatus(status.value)}
        >
          {busy === status.value ? 'Saving...' : status.label}
        </button>
      ))}
    </div>
  );
}

function DocumentGroups({ user, submission, onChanged }) {
  const fileGroups = Object.entries(submission.files || {});
  const editable = canEdit(user);
  const showFileStatus = user.role !== 'view';
  const [previewFile, setPreviewFile] = useState(null);

  return (
    <>
      {editable && <BackendUpload submission={submission} onChanged={onChanged} />}
      {fileGroups.map(([category, files]) => (
        <div className="photo-group" key={category}>
          <strong>{uploadCategories.find((item) => item.value === category)?.label || category}</strong>
          <div className="photo-grid">
            {(files || []).map((file) => (
              <div
                className={`photo-tile ${file.status} ${fileKind(file)}`}
                key={file.fileId || file.viewUrl || file.fileName}
                onDoubleClick={() => setPreviewFile(file)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setPreviewFile(file);
                }}
              >
                <FileThumb file={file} category={category} />
                {showFileStatus && <small>{file.status || 'active'}</small>}
                {editable && (
                  <button
                    type="button"
                    onClick={() =>
                      updateAdminSurveyFileStatus(submission.id, {
                        category,
                        fileId: file.fileId,
                        status: file.status === 'rejected' ? 'active' : 'rejected'
                      }).then(onChanged)
                    }
                  >
                    {file.status === 'rejected' ? 'Restore' : 'Reject'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </>
  );
}

function FileThumb({ file, category }) {
  const kind = fileKind(file);
  if (kind === 'image') {
    return (
      <>
        <img src={thumbnailUrl(file)} alt={file.fileName || category} loading="lazy" />
        <span>{file.fileName}</span>
      </>
    );
  }
  if (kind === 'video') {
    return (
      <>
        <video src={file.readUrl} muted preload="metadata" />
        <span>{file.fileName}</span>
      </>
    );
  }
  return (
    <>
      <div className="document-thumb">
        <strong>{fileExtension(file)}</strong>
      </div>
      <span>{file.fileName}</span>
    </>
  );
}

function FilePreviewModal({ file, onClose }) {
  const kind = fileKind(file);
  const title = file.fileName || 'Dokumen';

  return (
    <div className="admin-modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="admin-modal-panel">
        <div className="admin-modal-head">
          <div>
            <h3>{title}</h3>
            <span>{file.contentType || 'unknown file type'}</span>
          </div>
          <button type="button" onClick={onClose}>Close</button>
        </div>

        {kind === 'image' && <img className="large-preview-media" src={thumbnailUrl(file, 1280, 75)} alt={title} />}
        {kind === 'video' && <video className="large-preview-media" src={file.readUrl} controls autoPlay />}
        {kind === 'document' && (
          <div className="document-open-choice">
            <h4>Buka dokumen?</h4>
            <p>Pilih buka dengan aplikasi/browser atau download file.</p>
          </div>
        )}

        <div className="admin-modal-actions">
          <a href={file.readUrl} target="_blank" rel="noreferrer">{kind === 'document' ? 'Buka dengan Aplikasi' : 'Open New Tab'}</a>
          {kind === 'document' && <a href={file.downloadUrl || file.readUrl} download>Download</a>}
        </div>
      </div>
    </div>
  );
}

function BackendUpload({ submission, onChanged }) {
  const [category, setCategory] = useState('documentPhotos');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const signed = await requestAdminSignedUpload({ id: submission.id, category, file });
      await uploadFileToSignedUrl({ uploadUrl: signed.uploadUrl, file, contentType: signed.contentType || file.type || 'application/octet-stream' });
      await addAdminSurveyFile(submission.id, {
        category,
        fileName: file.name,
        contentType: signed.contentType || file.type || 'application/octet-stream',
        size: file.size,
        objectName: signed.objectName,
        gcsUri: signed.gcsUri,
        fileId: signed.fileId
      });
      await onChanged();
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backend-upload">
      <label>
        Upload Manual
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {uploadCategories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
      <label className="upload-file-button">
        {busy ? 'Uploading...' : 'Choose File'}
        <input type="file" accept="image/*,.heic,.heif,image/heic,image/heif,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" disabled={busy} onChange={upload} />
      </label>
      {error && <div className="admin-error">{error}</div>}
    </div>
  );
}

function AuditLog({ logs }) {
  if (!logs.length) return <div className="empty-log">Belum ada audit log.</div>;
  return (
    <div className="audit-list">
      {logs.map((log) => {
        const changes = auditChanges(log);
        return (
        <article className="audit-item" key={log.id}>
          <div>
            <strong>{readableAction(log.action)}</strong>
            <span>{log.changedAt} oleh {log.actorEmail || '-'} ({log.actorRole || '-'})</span>
          </div>
          <div className="audit-change-list">
            {changes.map((change) => (
              <div className="audit-change" key={change.field}>
                <b>{change.label}</b>
                <div>
                  <span>Sebelum</span>
                  <p>{change.before}</p>
                </div>
                <div>
                  <span>Sesudah</span>
                  <p>{change.after}</p>
                </div>
              </div>
            ))}
          </div>
          <details className="audit-raw">
            <summary>Data teknis asli</summary>
            <pre>{JSON.stringify(log.changes, null, 2)}</pre>
          </details>
        </article>
        );
      })}
    </div>
  );
}

function SettingsManagement() {
  const [projects, setProjects] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [form, setForm] = useState({
    activeProjectId: '__no_project__',
    submissionStatus: 'enabled',
    confirmationStatus: 'disabled',
    exportStatus: 'enabled',
    signedUrlExportTtl: '7d'
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function load() {
    const result = await getAdminSettings();
    setProjects(result.projects || []);
    setForm({
      activeProjectId: result.settings.activeProject?.id || '__no_project__',
      submissionStatus: result.settings.submissionStatus || 'enabled',
      confirmationStatus: result.settings.confirmationStatus || 'disabled',
      exportStatus: result.settings.exportStatus || 'enabled',
      signedUrlExportTtl: result.settings.signedUrlExportTtl || '7d'
    });
  }

  useEffect(() => {
    load().catch((loadError) => setError(loadError.message));
  }, []);

  async function saveSettings(event) {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      const result = await updateAdminSettings(form);
      setForm({
        activeProjectId: result.settings.activeProject?.id || '__no_project__',
        submissionStatus: result.settings.submissionStatus || 'enabled',
        confirmationStatus: result.settings.confirmationStatus || 'disabled',
        exportStatus: result.settings.exportStatus || 'enabled',
        signedUrlExportTtl: result.settings.signedUrlExportTtl || '7d'
      });
      setMessage('Settings disimpan.');
    } catch (saveError) {
      setError(saveError.message);
    }
  }

  async function addProject(event) {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      await createAdminProject({ name: projectName });
      setProjectName('');
      setMessage('Project ditambahkan.');
      await load();
    } catch (createError) {
      setError(createError.message);
    }
  }

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <h2>Settings</h2>
          <p>Active project dan submission status disimpan di Firestore `idx-survey-settings`.</p>
        </div>
      </div>

      <div className="settings-grid">
        <form className="admin-panel admin-form" onSubmit={saveSettings}>
          <h3>Survey Parameter</h3>
          <label>
            Project Active
            <select value={form.activeProjectId} onChange={(event) => setForm({ ...form, activeProjectId: event.target.value })}>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>
          <label>
            Submission Status
            <select value={form.submissionStatus} onChange={(event) => setForm({ ...form, submissionStatus: event.target.value })}>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          <label>
            Konfirmasi
            <select value={form.confirmationStatus} onChange={(event) => setForm({ ...form, confirmationStatus: event.target.value })}>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          <label className="switch-row">
            <span>
              Export
              <small>Jika disabled, hanya superadmin yang bisa export.</small>
            </span>
            <input
              type="checkbox"
              checked={form.exportStatus !== 'disabled'}
              onChange={(event) => setForm({ ...form, exportStatus: event.target.checked ? 'enabled' : 'disabled' })}
            />
          </label>
          <label>
            Masa Aktif Link Export
            <select value={form.signedUrlExportTtl} onChange={(event) => setForm({ ...form, signedUrlExportTtl: event.target.value })}>
              {signedUrlTtlOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          {error && <div className="admin-error">{error}</div>}
          {message && <div className="admin-success">{message}</div>}
          <button type="submit">Save Settings</button>
        </form>

        <form className="admin-panel admin-form" onSubmit={addProject}>
          <h3>Tambah Project</h3>
          <label>
            Project Name
            <input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Nama project" />
          </label>
          <button type="submit">Add Project</button>
          <div className="project-list">
            {projects.map((project) => (
              <span key={project.id}>{project.name}</span>
            ))}
          </div>
        </form>
      </div>
    </section>
  );
}

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      await changeAdminPassword({ currentPassword, newPassword });
      setMessage('Password berubah. Silakan login ulang.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (changeError) {
      setError(changeError.message);
    }
  }

  return (
    <form className="admin-panel admin-form" onSubmit={submit}>
      <h2>Change Password</h2>
      <label>
        Current Password
        <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
      </label>
      <label>
        New Password
        <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
      </label>
      {error && <div className="admin-error">{error}</div>}
      {message && <div className="admin-success">{message}</div>}
      <button type="submit">Save Password</button>
    </form>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', name: '', role: 'user', password: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function load() {
    const result = await getAdminUsers();
    setUsers(result.items);
  }

  useEffect(() => {
    load().catch((loadError) => setError(loadError.message));
  }, []);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      await createAdminUser(form);
      setForm({ email: '', name: '', role: 'user', password: '' });
      setMessage('User dibuat.');
      await load();
    } catch (createError) {
      setError(createError.message);
    }
  }

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <h2>Users</h2>
          <p>User disimpan di Firestore database `idx-survey-settings`.</p>
        </div>
      </div>
      <div className="user-grid">
        <form className="admin-panel admin-form" onSubmit={submit}>
          <h3>Tambah User</h3>
          <label>
            Email
            <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </label>
          <label>
            Nama
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            Role
            <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
              <option value="user">user</option>
              <option value="view">view</option>
              <option value="superadmin">superadmin</option>
            </select>
          </label>
          <label>
            Password
            <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
          </label>
          {error && <div className="admin-error">{error}</div>}
          {message && <div className="admin-success">{message}</div>}
          <button type="submit">Create User</button>
        </form>
        <div className="admin-panel table-panel">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((item) => (
                <tr key={item.email}>
                  <td>{item.email}</td>
                  <td>{item.role}</td>
                  <td>{item.active ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminApp;
